import { useEffect, useState } from "react";
import ToggleSwitch from "../../components/ToggleSwitch";
import DataTable, { Column } from "../../components/DataTable";
import { toast } from "sonner";
import SchoolBranchApis from "../../api/schoolBranchApis";
import { Subjects } from "../../types/schoolBranchType";
import { useSelector } from "react-redux";
import { RootState } from "../../store";

export default function SubjectsTab() {
  const [subjects, setSubjects] = useState<Subjects[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const branchId = useSelector(
    (state: RootState) => state.access.schoolBranch?.schoolBranchId
  );

  const fetchSubjects = async () => {
    try {
      setLoading(true);
      const response = await SchoolBranchApis.getSubjects(branchId);
      setSubjects(response.data);
    } catch (err) {
      toast.error("Failed to load academic cycles");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubjects();
  }, [branchId]);

  const handleToggle = async (subject: Subjects) => {
    try {
      setLoading(true);
      await SchoolBranchApis.toggleSubjects(branchId, subject.subjectId!);
      const message = subject.active
        ? "Subject disabled successfully"
        : "Subject enabled successfully";

      toast.success(message);
      await fetchSubjects();
    } catch (error) {
      toast.error("Subjects toggle failed");
    } finally {
      setLoading(false);
    }
  };

  const columns: Column<Subjects>[] = [
    {
      label: "Subject Name",
      accessor: "name",
      render: (item) => (
        <div className="font-bold text-sm text-neutral-400">
          {item.subjectName}
        </div>
      ),
    },
    {
      label: "Enable/Disable",
      accessor: "active",
      searchable: false,
      render: (item) => (
        <ToggleSwitch
          defaultChecked={item.active}
          onToggle={() => handleToggle(item)}
        />
      ),
    },
  ];

  return (
    <div className="px-4 md:px-9 py-7 flex flex-col gap-8 overflow-x-auto">
      <span className="text-xs font-medium text-error-500">
        *Please enable the subjects taught in your school. In case if you want
        to delete or modify an existing subject then please raise a ticket for
        the same in the Help Desk Section.
      </span>

      <div className="overflow-x-auto">
        <div className="hidden lg:grid lg:grid-cols-2 lg:gap-x-12">
          {/* Split into two columns on large screens */}
          <DataTable
            columns={columns}
            data={subjects
              ?.sort((a, b) => a.subjectName.localeCompare(b.subjectName))
              ?.slice(0, Math.ceil(subjects.length / 2))}
            isLoading={loading}
          />
          <DataTable
            columns={columns}
            data={subjects
              ?.sort((a, b) => a.subjectName.localeCompare(b.subjectName))
              ?.slice(Math.ceil(subjects.length / 2))}
            isLoading={loading}
          />
        </div>

        {/* Single table for small screens */}
        <div className="lg:hidden">
          <DataTable columns={columns} data={subjects} isLoading={loading} />
        </div>
      </div>
    </div>
  );
}
